import React, { useState, useEffect } from 'react';
import { LineChart, RadarChart, Radar, PolarGrid, PolarRadiusAxis, PolarAngleAxis, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ReactDOM from 'react-dom/client';
import './index.css';  // This imports both Tailwind and base styles

function PlayerAnalysis() {
  const [playerData, setPlayerData] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teams, setTeams] = useState([]);

  // Load CSV data
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching CSV data...');
        
        // Fetch player stats
        const statsResponse = await fetch('./womens_stats_2024_progress_round1_noteam.csv');
        if (!statsResponse.ok) throw new Error('Network response was not ok');
        const statsCsv = await statsResponse.text();
        console.log('Stats CSV loaded, first 100 chars:', statsCsv.substring(0, 100));
        
        // Fetch player dictionary
        const dictResponse = await fetch('./dictionary.csv');
        if (!dictResponse.ok) throw new Error('Network response was not ok');
        const dictCsv = await dictResponse.text();
        console.log('Dictionary CSV loaded, first 100 chars:', dictCsv.substring(0, 100));
        
        // Parse CSVs
        const parseCsv = (csv) => {
          const lines = csv.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());
          return lines.slice(1).map(line => {
            const values = line.split(',').map(value => value.trim());
            return headers.reduce((obj, header, index) => {
              obj[header] = values[index];
              return obj;
            }, {});
          });
        };

        const statsData = parseCsv(statsCsv);
        const dictData = parseCsv(dictCsv);
        
        console.log('Sample stat row:', statsData[0]);
        console.log('Sample dictionary row:', dictData[0]);
        console.log('Total players in dictionary:', dictData.length);
        console.log('Unique positions:', [...new Set(dictData.map(d => d['Primary Position']))]);

        // Merge data based on Player and Player Name
        const mergedData = statsData.map(stat => {
          const playerDict = dictData.find(dict => dict['Player Name'] === stat.Player);
          return { ...stat, ...playerDict };
        });

        const uniqueTeams = [...new Set(dictData.map(dict => dict['Team']))].sort();
        setTeams(uniqueTeams);
        
        // Get unique players
        const uniquePlayers = [...new Set(mergedData.map(row => row.Player))];
        setPlayers(uniquePlayers);
        setPlayerData(mergedData);
      } catch (error) {
        console.error('Error fetching the CSV file:', error);
      }
    };

    fetchData();
  }, []);

  // Update stats when player is selected
  useEffect(() => {
    if (selectedPlayer && playerData.length > 0) {
      const playerMatches = playerData.filter(row => row.Player === selectedPlayer);
      const position = playerMatches[0]?.['Primary Position'];  // Get player's position
      
      // Calculate position averages - add this section
      const positionPlayers = playerData.filter(p => p['Primary Position'] === position);
      const positionAverage = {
        kicks: calculateAverage(positionPlayers, 'K'),
        handballs: calculateAverage(positionPlayers, 'HB'),
        marks: calculateAverage(positionPlayers, 'M'),
        tackles: calculateAverage(positionPlayers, 'T'),
        contested: calculateAverage(positionPlayers, 'CM')
      };

      const stats = {
        games: playerMatches.length,
        team: playerMatches[0]?.Team || 'Unknown',
        primaryPosition: playerMatches[0]?.['Primary Position'] || 'Unknown',
        secondaryPosition: playerMatches[0]?.['Secondary Position'] || '-',
        avgFantasy: calculateAverage(playerMatches, 'RP'),
        totalDisposals: playerMatches.reduce((acc, curr) =>
          acc + (Number(curr.K) + Number(curr.HB)), 0),
        avgDisposals: (
          Number(calculateAverage(playerMatches, 'K')) + 
          Number(calculateAverage(playerMatches, 'HB'))
        ).toFixed(1),
        avgKicks: calculateAverage(playerMatches, 'K'),
        avgHandballs: calculateAverage(playerMatches, 'HB'),
        avgMarks: calculateAverage(playerMatches, 'M'),
        avgTackles: calculateAverage(playerMatches, 'T'),
        avgContested: calculateAverage(playerMatches, 'CM'),
        matchLog: playerMatches.sort((a, b) => Number(a.Round) - Number(b.Round)),
        performanceData: playerMatches.map(match => ({
          round: Number(match.Round),
          fantasy: Number(match.RP),
          disposals: Number(match.K) + Number(match.HB)
        })),
        positionAverage: positionAverage  
      };
      
      setPlayerStats(stats);
    }
  }, [selectedPlayer, playerData]);

// Add this useEffect back to filter players based on team selection
  useEffect(() => {
    if (playerData.length > 0) {
      const filteredPlayers = selectedTeam
        ? [...new Set(playerData.filter(row => row.Team === selectedTeam).map(row => row.Player))].sort()
        : [...new Set(playerData.map(row => row.Player))].sort();
      setPlayers(filteredPlayers);
      setSelectedPlayer(null);
      setPlayerStats(null);
    }
  }, [selectedTeam, playerData]);

  const calculateAverage = (data, field) => {
    const sum = data.reduce((acc, curr) => acc + Number(curr[field] || 0), 0);
    return (sum / data.length).toFixed(1);
  };

  const calculateSum = (data, field) => {
    return data.reduce((acc, curr) => acc + Number(curr[field] || 0), 0);
  };

  const PlayerRadarChart = ({ playerStats, positionAverage }) => {
    const data = [
      {
        stat: "K",
        fullName: "Kicks",
        player: Number(playerStats.avgKicks),
        average: Number(positionAverage.kicks)
      },
      {
        stat: "HB",
        fullName: "Handballs",
        player: Number(playerStats.avgHandballs),
        average: Number(positionAverage.handballs)
      },
      {
        stat: "M",
        fullName: "Marks",
        player: Number(playerStats.avgMarks),
        average: Number(positionAverage.marks)
      },
      {
        stat: "T",
        fullName: "Tackles",
        player: Number(playerStats.avgTackles),
        average: Number(positionAverage.tackles)
      },
      {
        stat: "CM",
        fullName: "Contested Marks",
        player: Number(playerStats.avgContested),
        average: Number(positionAverage.contested)
      }
    ];

    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-gray-800/90 px-4 py-3 rounded-lg border border-gray-700/50">
            <p className="text-white font-semibold mb-2">{payload[0].payload.fullName}</p>
            <p className="text-emerald-400">Player: {payload[0].value.toFixed(1)}</p>
            <p className="text-blue-400">Position Avg: {payload[1].value.toFixed(1)}</p>
          </div>
        );
      }
      return null;
    };
  
    return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart outerRadius="80%" data={data}>
          <PolarGrid stroke="rgba(255,255,255,0.2)" />
          <PolarAngleAxis 
            dataKey="stat" 
            stroke="#fff"
            tick={{ fill: '#fff' }}
          />
          <PolarRadiusAxis 
            stroke="rgba(255,255,255,0.2)"
            tick={{ fill: '#fff' }}
            domain={[0, 12]}
          />
          <Radar
            name="Player"
            dataKey="player"
            stroke="#10B981"
            fill="#10B981"
            fillOpacity={0.5}
          />
          <Radar
            name="Position Average"
            dataKey="average"
            stroke="#60A5FA"
            fill="#60A5FA"
            fillOpacity={0.3}
          />
          <Legend />
          <Tooltip content={<CustomTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

return (
  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-3 sm:p-6">
    <div className="mb-8 sm:mb-12 relative">
      <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob"></div>
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob animation-delay-2000"></div>
    </div>

    {/* Main Grid with glassmorphism */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 lg:gap-8">
      {/* Sidebar */}
      <div className="col-span-1 lg:col-span-3 space-y-3 sm:space-y-4 lg:space-y-6">
        {/* Filters Card */}
        <div className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
            Filters
          </h2>
          <div>
      <label className="block text-gray-400 mb-2 text-sm">Select Team</label>
      <select 
        className="w-full bg-gray-700/50 p-3 rounded-xl border border-gray-600/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300 mb-4"
        onChange={(e) => setSelectedTeam(e.target.value)}
        value={selectedTeam}
      >
        <option value="">All Teams</option>
        {teams.map(team => (
          <option key={team} value={team}>{team}</option>
        ))}
      </select>
      
      <label className="block text-gray-400 mb-2 text-sm">Select Player</label>
      <select 
        className="w-full bg-gray-700/50 p-3 rounded-xl border border-gray-600/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300"
        onChange={(e) => setSelectedPlayer(e.target.value)}
        value={selectedPlayer || ''}
      >
        <option value="">Select Player</option>
        {players.map(player => (
          <option key={player} value={player}>{player}</option>
        ))}
      </select>
    </div>
        </div>

        {playerStats && (
          <>
            {/* Quick Stats Card */}
            <div className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                Quick Stats
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Team</span>
                  <span className="text-2xl font-bold text-emerald-400">{playerStats.team}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Games</span>
                  <span className="text-2xl font-bold text-emerald-400">{playerStats.games}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Avg Fantasy</span>
                  <span className="text-2xl font-bold text-blue-400">{playerStats.avgFantasy}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Primary Position</span>
                  <span className="text-2xl font-bold text-blue-400">{playerStats.primaryPosition}</span>
                </div>
              </div>
            </div>

            {/* Position Comparison Card */}
            <div className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                Position Comparison
              </h2>
              <div className="h-[300px] sm:h-[400px]">
                <PlayerRadarChart 
                  playerStats={playerStats} 
                  positionAverage={playerStats.positionAverage}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="col-span-1 lg:col-span-9 space-y-3 sm:space-y-4 lg:space-y-8">
        {playerStats && (
          <>
            {/* Performance Chart */}
            <div className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-8 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                Performance Trends
              </h2>
              <div className="h-60 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={playerStats.performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="round" stroke="#fff" />
                      <YAxis stroke="#fff" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(17, 24, 39, 0.9)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px'
                        }} 
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="fantasy" 
                        name="Fantasy" 
                        stroke="#10B981" 
                        strokeWidth={3}
                        dot={{ r: 6, strokeWidth: 2 }}
                        activeDot={{ r: 8, strokeWidth: 2 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="disposals" 
                        name="Disposals" 
                        stroke="#60A5FA" 
                        strokeWidth={3}
                        dot={{ r: 6, strokeWidth: 2 }}
                        activeDot={{ r: 8, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
              {['Disposals', 'Marks', 'Tackles'].map((stat, index) => (
                <div key={stat} className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <h3 className="text-lg font-semibold mb-2">{stat}</h3>
                    <div className={`text-4xl font-bold ${
                      index === 0 ? 'text-emerald-400' : 
                      index === 1 ? 'text-blue-400' : 
                      'text-purple-400'
                    }`}>
                      {index === 0 ? playerStats.avgDisposals : playerStats[`avg${stat}`]}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">Season Average</div>
                </div>
              ))}
            </div>

            {/* Match Log */}
            <div className="backdrop-blur-lg bg-gray-800/50 p-4 sm:p-8 rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-lg">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                Match Log
              </h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="min-w-[600px] px-4 sm:px-0">
                <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700/50">
                        <th className="p-3">Round</th>
                        <th className="p-3">K</th>
                        <th className="p-3">HB</th>
                        <th className="p-3">M</th>
                        <th className="p-3">T</th>
                        <th className="p-3">CM</th>
                        <th className="p-3">AF</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      {playerStats.matchLog.map((match, index) => (
                        <tr 
                          key={index} 
                          className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors duration-150"
                        >
                          <td className="p-3">{match.Round}</td>
                          <td className="p-3">{match.K}</td>
                          <td className="p-3">{match.HB}</td>
                          <td className="p-3">{match.M}</td>
                          <td className="p-3">{match.T}</td>
                          <td className="p-3">{match.CM}</td>
                          <td className="p-3 font-semibold">{match.RP}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);
}

export default PlayerAnalysis;
